// components/TiptapEditor.tsx
import { useEditor, EditorContent, type Editor } from '@tiptap/react'
import { Extension, posToDOMRect } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import StarterKit from '@tiptap/starter-kit'
import { Input } from '@univdiam/ui/components/input'
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type FC, type ReactNode } from 'react'
import Placeholder from '@tiptap/extension-placeholder'
import Link from '@tiptap/extension-link'
import { Bold, Italic, List, Link2, Link2Off, Check, X, Pencil, ExternalLink } from 'lucide-react'
import { cn } from '@univdiam/ui/lib/utils'

type RichTextEditorProps = {
    placeholder?: string
    value: string
    onChange: (value: string) => void
    onEnter?: () => void
    className?: string
    editorClass?: string
    /** Pasted images go here as files instead of into the document. */
    onPasteFiles?: (files: File[]) => void
}

/** Schemes the link mark accepts. Mirrors renderRichText()'s ALLOWED_URI_REGEXP. */
const SAFE_PROTOCOLS = ['http', 'https', 'mailto', 'tel']
const DANGEROUS_SCHEME = /^\s*(javascript|data|vbscript|file):/i
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i

/**
 * Accept what a human pastes. Bare domains get https://, mail addresses get
 * mailto:. Returns null when the value can never be a safe href, so the caller
 * refuses rather than writing a javascript: URL into the document.
 */
const normalizeUrl = (raw: string): string | null => {
    const value = raw.trim()
    if (!value) return null
    if (DANGEROUS_SCHEME.test(value)) return null

    let candidate = value
    if (!HAS_SCHEME.test(candidate)) {
        candidate = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate)
            ? `mailto:${candidate}`
            : `https://${candidate}`
    }

    try {
        const url = new URL(candidate)
        const scheme = url.protocol.replace(':', '').toLowerCase()
        return SAFE_PROTOCOLS.includes(scheme) ? url.toString() : null
    } catch {
        return null
    }
}

/**
 * While the link input has focus the editor is blurred, so the browser stops
 * painting the native selection and the text you are about to link looks
 * deselected. Paint it ourselves for the duration.
 */
/**
 * The Link mark declares `inclusive() { return this.options.autolink }`, so
 * enabling autolink makes it inclusive: text typed at the end of a link joins
 * the link, and everything you type afterwards turns blue. Override it — typing
 * after a link is plain text. Autolink still works, because it rescans the word
 * on whitespace rather than extending the stored mark.
 *
 * Defined at module scope: Mark.extend() builds a new extension, and doing that
 * inside the component body would rebuild it on every render.
 */
const NonInclusiveLink = Link.extend({ inclusive: false })

const highlightKey = new PluginKey<DecorationSet>('linkSelectionHighlight')

const SelectionHighlight = Extension.create({
    name: 'linkSelectionHighlight',
    addProseMirrorPlugins () {
        return [
            new Plugin({
                key: highlightKey,
                state: {
                    init: () => DecorationSet.empty,
                    apply (tr, current) {
                        const meta = tr.getMeta(highlightKey) as { from: number, to: number } | null | undefined
                        if (meta === null) return DecorationSet.empty
                        if (meta) {
                            if (meta.from === meta.to) return DecorationSet.empty
                            return DecorationSet.create(tr.doc, [
                                Decoration.inline(meta.from, meta.to, { class: 'bg-blue-200/70 rounded-[2px]' }),
                            ])
                        }
                        return current.map(tr.mapping, tr.doc)
                    },
                },
                props: {
                    decorations (state) {
                        return highlightKey.getState(state)
                    },
                },
            }),
        ]
    },
})

/**
 * The link mark is non-inclusive, so a caret sitting immediately after the last
 * character of a link is not "inside" it and editor.isActive('link') is false.
 * That is the position you land on when you click the end of a link, so look at
 * the node just before the caret too.
 */
const activeLinkHref = (editor: Editor | null): string | undefined => {
    if (!editor) return undefined
    if (editor.isActive('link')) return editor.getAttributes('link').href as string | undefined
    const { $from, empty } = editor.state.selection
    if (!empty) return undefined
    const mark = $from.nodeBefore?.marks.find(m => m.type.name === 'link')
    return mark?.attrs.href as string | undefined
}

const ToolbarButton = ({
    onClick, active, title, children,
}: { onClick: () => void; active?: boolean; title: string; children: ReactNode }) => (
    <button
        type='button'
        // Mousedown would blur the editor and collapse the selection you are
        // about to format or link.
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
        aria-label={title}
        aria-pressed={!!active}
        className={cn(
            'bg-[#F6F6F6] w-[20px] h-[20px] flex items-center justify-center cursor-pointer rounded-[4px] hover:bg-gray-200 transition-colors',
            active && 'bg-gray-200'
        )}
        title={title}
    >
        {children}
    </button>
)

const BubbleButton = ({
    onClick, title, children, danger,
}: { onClick: () => void; title: string; children: ReactNode; danger?: boolean }) => (
    <button
        type='button'
        onClick={onClick}
        aria-label={title}
        title={title}
        className={cn(
            'w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 transition-colors shrink-0',
            danger && 'hover:bg-red-50 hover:text-red-600'
        )}
    >
        {children}
    </button>
)

const RichTextEditor: FC<RichTextEditorProps> = ({ placeholder, value, onChange, onEnter, className, editorClass, onPasteFiles }) => {
    const [isEditingLink, setIsEditingLink] = useState(false)
    const [linkUrl, setLinkUrl] = useState('')
    const [linkError, setLinkError] = useState('')
    const [isFocused, setIsFocused] = useState(false)
    const [bubblePos, setBubblePos] = useState<{ top: number, left: number } | null>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)

    // useEditor builds editorProps once, so handleKeyDown closes over the FIRST
    // render forever — where `editor` is still null. Anything that handler reads
    // must come through a ref, or it silently sees stale values: `isActive` was
    // always undefined (so Enter inside a list never made a list item) and
    // `splitBlock()` never ran (so Shift+Enter did nothing).
    const editorRef = useRef<Editor | null>(null)
    const onEnterRef = useRef<(() => void) | undefined>(onEnter)
    const onPasteFilesRef = useRef(onPasteFiles)
    const isEditingLinkRef = useRef(false)
    const startLinkRef = useRef<() => void>(() => {})

    const editor = useEditor({
        extensions: [
            StarterKit,
            SelectionHighlight,
            Placeholder.configure({
                placeholder: placeholder ? placeholder : 'Type here ...'
            }),
            NonInclusiveLink.configure({
                openOnClick: false,
                autolink: true,
                // Pasting a URL over selected text links it in place — no UI at all.
                linkOnPaste: true,
                defaultProtocol: 'https',
                protocols: SAFE_PROTOCOLS,
                // Without this, links look like body text while editing and the
                // only way to find one is to click and watch the toolbar.
                HTMLAttributes: {
                    class: 'text-blue-600 underline underline-offset-2 decoration-blue-600/40 cursor-pointer',
                },
            })
        ],
        editorProps: {
            attributes: {
                class: cn('min-h-[100px] w-full text-sm focus:outline-none', editorClass),
            },
            handlePaste: (_view, event) => {
                const handler = onPasteFilesRef.current
                const images = Array.from(event.clipboardData?.files ?? []).filter(f => f.type.startsWith('image/'))
                if (!handler || images.length === 0) return false
                event.preventDefault()
                handler(images)
                return true
            },
            handleKeyDown: (_view, event) => {
                if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                    event.preventDefault()
                    startLinkRef.current()
                    return true
                }
                // The link input lives outside the editor, but if focus ever lands
                // back here while it is open, Enter must not split the block behind it.
                if (isEditingLinkRef.current && (event.key === 'Enter' || event.key === 'Escape')) {
                    event.preventDefault()
                    return true
                }
                if (event.key === 'Enter') {
                    const live = editorRef.current
                    const isList = live?.isActive('bulletList') || live?.isActive('orderedList')
                    if (isList) {
                        // let the list extension add the next item
                        return false
                    }
                    // Shift+Enter is always a line break, and never submits.
                    if (event.shiftKey) {
                        live?.commands.splitBlock()
                        return true
                    }

                    const submit = onEnterRef.current
                    if (submit && live) {
                        // Enter once -> blank line. Enter again on that blank line ->
                        // send. Refusing to submit when the doc is otherwise empty
                        // keeps a stray Enter in an empty box from sending nothing.
                        const { $from, empty } = live.state.selection
                        const caretOnBlankLine = empty && $from.parent.content.size === 0
                        const hasContent = live.getText().trim().length > 0
                        if (caretOnBlankLine && hasContent) {
                            event.preventDefault()
                            submit()
                            return true
                        }
                    }
                    // otherwise: default Enter, which splits the block (newline)
                }
                return false
            }
        },
        content: value || '',
        onUpdate({ editor }) {
            onChange?.(editor.getHTML())
        },
    })

    const setHighlight = useCallback((range: { from: number, to: number } | null) => {
        if (!editor) return
        editor.view.dispatch(editor.view.state.tr.setMeta(highlightKey, range))
    }, [editor])

    const closeLinkEditor = useCallback((refocus = true) => {
        setIsEditingLink(false)
        setLinkError('')
        setHighlight(null)
        if (refocus) editor?.chain().focus().run()
    }, [editor, setHighlight])

    const applyLink = useCallback((href: string) => {
        if (!editor) return
        // With nothing selected there is no text to carry the mark, so insert the
        // URL as its own linked text rather than silently doing nothing.
        if (editor.state.selection.empty && !activeLinkHref(editor)) {
            editor.chain().focus()
                .insertContent({ type: 'text', text: href, marks: [{ type: 'link', attrs: { href } }] })
                .run()
        } else {
            editor.chain().focus().extendMarkRange('link').setLink({ href }).run()
        }
    }, [editor])

    const openLinkEditor = useCallback((prefill: string) => {
        if (!editor) return
        const { from, to } = editor.state.selection
        setHighlight(from === to ? null : { from, to })
        setLinkUrl(prefill)
        setLinkError('')
        setIsEditingLink(true)
        // Bubble mounts on the next paint; focus once it exists.
        setTimeout(() => inputRef.current?.focus(), 0)
    }, [editor, setHighlight])

    /**
     * The fast path: a valid URL already on the clipboard is applied with no UI.
     * Anything else — empty clipboard, junk, or an existing link to edit —
     * falls back to the inline input.
     */
    const startLink = useCallback(async () => {
        if (!editor) return

        const existing = activeLinkHref(editor)
        if (existing) {
            openLinkEditor(existing)
            return
        }

        try {
            const clip = await navigator.clipboard.readText()
            const href = normalizeUrl(clip)
            if (href) {
                applyLink(href)
                return
            }
        } catch {
            // clipboard blocked or unavailable — fall through to the input
        }
        openLinkEditor('')
    }, [editor, applyLink, openLinkEditor])

    const saveLink = useCallback(() => {
        if (!editor) return
        if (linkUrl.trim() === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run()
            closeLinkEditor()
            return
        }
        const href = normalizeUrl(linkUrl)
        if (!href) {
            setLinkError('Use a valid http, https, mailto or tel link.')
            return
        }
        applyLink(href)
        closeLinkEditor()
    }, [editor, linkUrl, applyLink, closeLinkEditor])

    const removeLink = useCallback(() => {
        if (!editor) return
        editor.chain().focus().extendMarkRange('link').unsetLink().run()
        closeLinkEditor()
    }, [editor, closeLinkEditor])

    const openLinkInTab = useCallback(() => {
        const href = activeLinkHref(editor)
        if (href) window.open(href, '_blank', 'noopener,noreferrer')
    }, [editor])

    // Mirror live values into the refs that the (frozen) editorProps closure reads.
    useEffect(() => { editorRef.current = editor ?? null }, [editor])
    useEffect(() => { onEnterRef.current = onEnter }, [onEnter])
    useEffect(() => { onPasteFilesRef.current = onPasteFiles }, [onPasteFiles])
    useEffect(() => { isEditingLinkRef.current = isEditingLink }, [isEditingLink])
    useEffect(() => { startLinkRef.current = () => { void startLink() } }, [startLink])

    useEffect(() => {
        if (!editor) return
        const onFocus = () => setIsFocused(true)
        const onBlur = () => setIsFocused(false)
        editor.on('focus', onFocus)
        editor.on('blur', onBlur)
        return () => { editor.off('focus', onFocus); editor.off('blur', onBlur) }
    }, [editor])

    useEffect(() => {
        if (editor && value !== undefined && editor.getHTML() !== value) {
            editor.commands.setContent(value)
        }
    }, [value, editor])

    const currentHref = activeLinkHref(editor) ?? ''
    const onLink = !!currentHref
    // While the link input is open the editor is blurred, so isFocused alone
    // cannot keep the bubble on screen.
    const showBubble = isEditingLink || (isFocused && onLink)

    // useEditor re-renders on every transaction, so these track the live selection.
    const selFrom = editor?.state.selection.from ?? 0
    const selTo = editor?.state.selection.to ?? 0

    // Anchor the bubble above the selection, in coordinates relative to the
    // wrapper. Nothing here depends on the editor being focused, which is why
    // it survives the click that opens the link input.
    useLayoutEffect(() => {
        const host = wrapperRef.current
        if (!editor || !host || !showBubble) return
        const rect = posToDOMRect(editor.view, selFrom, selTo)
        const box = host.getBoundingClientRect()
        const top = rect.top - box.top - 8
        const left = Math.max(0, rect.left - box.left)
        setBubblePos(prev => (prev && prev.top === top && prev.left === left) ? prev : { top, left })
    }, [editor, showBubble, selFrom, selTo, isEditingLink])

    return (
        <div ref={wrapperRef} className="relative w-full flex flex-col gap-1">
            {showBubble && bubblePos && (
                <div
                    style={{ top: bubblePos.top, left: bubblePos.left }}
                    className="absolute z-50 -translate-y-full flex items-center gap-1 rounded-md border border-[#DBDBDB] bg-white p-1 shadow-md"
                    // Buttons in here must not blur the editor: the bubble's own
                    // visibility depends on the editor's focus, so a blur would
                    // unmount it between mousedown and click. The link input is the
                    // one child that does need focus.
                    onMouseDown={(e) => {
                        if (!(e.target as HTMLElement).closest('input')) e.preventDefault()
                    }}
                    // This popover renders INSIDE any surrounding <form> (ChatSection,
                    // CreateOrder, AddProduct*). Enter in a text input there is implicit
                    // form submission — it sent chat messages and reloaded the discussion
                    // page. Swallow the key here, in the capture phase, before it reaches
                    // the input, the editor, or the form.
                    onKeyDownCapture={(e) => {
                        if (e.key !== 'Enter' && e.key !== 'Escape') return
                        e.preventDefault()
                        e.stopPropagation()
                        if (!isEditingLink) return
                        if (e.key === 'Enter') saveLink()
                        else closeLinkEditor()
                    }}
                >
                    {isEditingLink ? (
                        <>
                            <Link2 className="w-3.5 h-3.5 text-gray-400 ml-1 shrink-0" />
                            <div className="flex flex-col">
                                <Input
                                    ref={inputRef}
                                    value={linkUrl}
                                    onChange={(e) => { setLinkUrl(e.target.value); setLinkError('') }}
                                    placeholder="example.com"
                                    aria-invalid={!!linkError}
                                    className="h-7 w-56 border-0 shadow-none text-sm focus-visible:ring-0"
                                    // Enter/Escape are handled by the wrapper's capture handler.
                                    onKeyDown={(e) => e.stopPropagation()}
                                />
                                {linkError && <p className="text-[10px] text-red-600 px-3 pb-1">{linkError}</p>}
                            </div>
                            <BubbleButton onClick={saveLink} title="Apply link">
                                <Check className="w-3.5 h-3.5" />
                            </BubbleButton>
                            <BubbleButton onClick={() => closeLinkEditor()} title="Cancel">
                                <X className="w-3.5 h-3.5" />
                            </BubbleButton>
                        </>
                    ) : (
                        <>
                            <a
                                href={currentHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="max-w-[220px] truncate px-2 text-xs text-blue-600 hover:underline"
                                title={currentHref}
                            >
                                {currentHref}
                            </a>
                            <BubbleButton onClick={openLinkInTab} title="Open in new tab">
                                <ExternalLink className="w-3.5 h-3.5" />
                            </BubbleButton>
                            <BubbleButton onClick={() => openLinkEditor(currentHref)} title="Edit link">
                                <Pencil className="w-3.5 h-3.5" />
                            </BubbleButton>
                            <BubbleButton onClick={removeLink} title="Remove link" danger>
                                <Link2Off className="w-3.5 h-3.5" />
                            </BubbleButton>
                        </>
                    )}
                </div>
            )}

            {/* Toolbar */}
            <div className="flex gap-[8px] px-[2px] py-[2px] items-center">
                <ToolbarButton onClick={() => editor?.chain().focus().toggleBold().run()} active={editor?.isActive('bold')} title="Bold">
                    <Bold className="w-[10px] h-[10px]" strokeWidth={2.5} />
                </ToolbarButton>
                <ToolbarButton onClick={() => editor?.chain().focus().toggleItalic().run()} active={editor?.isActive('italic')} title="Italic">
                    <Italic className="w-[10px] h-[10px]" strokeWidth={2.5} />
                </ToolbarButton>
                <ToolbarButton onClick={() => editor?.chain().focus().toggleBulletList().run()} active={editor?.isActive('bulletList')} title="Bullet list">
                    <List className="w-[12px] h-[12px]" strokeWidth={2.5} />
                </ToolbarButton>
                <ToolbarButton onClick={() => void startLink()} active={onLink} title={onLink ? 'Edit link (Ctrl+K)' : 'Insert link (Ctrl+K)'}>
                    <Link2 className="w-[10px] h-[10px]" strokeWidth={2.5} />
                </ToolbarButton>
            </div>

            {/* Editor Box */}
            <div className={cn("border border-[#DBDBDB] rounded-[6px] w-full", className)}>
                <EditorContent
                    editor={editor}
                    className="!border-none"
                />
            </div>
        </div>
    )
}

export default RichTextEditor
